import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { experienceService } from '@/services/experience.service';
import { mediaUrl } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { FavoriteButton } from '@/components/public/FavoriteButton';
import { T } from '@/components/common/T';
import { ArrowRight, MapPin, Clock, CalendarDays, ImageIcon, Ticket, Sparkles } from 'lucide-react';

const euro = (n) => `€${Number(n || 0).toLocaleString('it-IT', { maximumFractionDigits: 2 })}`;

function nextSession(e) {
  const ss = (e.sessions || []).map((s) => s.start).filter(Boolean).sort();
  const fut = ss.find((s) => new Date(s) >= new Date());
  const iso = fut || ss[0];
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' }) + ' · ' +
      d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  } catch { return null; }
}

/**
 * Card a forma di BIGLIETTO: immagine evento + perforazione + stub con QR reale.
 * @param {object} e            esperienza pubblica
 * @param {boolean} compact     versione più piccola (per griglie a 4 colonne, es. home)
 * @param {boolean} showFavorite mostra il cuore preferiti (in alto a SINISTRA)
 */
export function ExperienceTicketCard({ e, compact = false, showFavorite = false }) {
  const url = `${window.location.origin}/esperienze/${e.slug || e.id}`;
  const date = nextSession(e);
  const headerH = compact ? 'h-36' : 'h-44';
  const qrSize = compact ? 52 : 64;
  return (
    <Link
      to={`/esperienze/${e.slug || e.id}`}
      className="group block h-full overflow-hidden rounded-[22px] border border-border bg-card shadow-sm transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      data-testid={`experience-ticket-${e.id}`}
    >
      {/* Testata evento */}
      <div className={cn('relative overflow-hidden bg-muted', headerH)}>
        {e.cover_image ? (
          <img src={mediaUrl(e.cover_image)} alt={e.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        ) : (
          <div className="flex h-full w-full items-center justify-center"><ImageIcon className="h-8 w-8 text-muted-foreground" /></div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
        {showFavorite ? (
          <FavoriteButton structureId={e.id} structureType="experience" size="sm" className="absolute left-3 top-3 z-10" />
        ) : (
          e.category && (
            <span className="absolute left-3 top-3 flex items-center gap-1 rounded-full bg-background/90 px-2.5 py-1 text-xs font-medium text-foreground backdrop-blur">
              <Ticket className="h-3 w-3 text-primary" /><T>{e.category}</T>
            </span>
          )
        )}
        <span className="absolute right-3 top-3 rounded-full bg-primary px-3 py-1 text-xs font-bold text-primary-foreground shadow-sm">{e.from_price > 0 ? <><T>da</T>{` ${euro(e.from_price)}`}</> : <T>Gratis</T>}</span>
      </div>

      {/* Perforazione con tacche laterali */}
      <div className="relative h-0">
        <span className="absolute -left-2.5 top-1/2 h-5 w-5 -translate-y-1/2 rounded-full border border-border bg-background" />
        <span className="absolute -right-2.5 top-1/2 h-5 w-5 -translate-y-1/2 rounded-full border border-border bg-background" />
        <div className="absolute inset-x-5 top-1/2 -translate-y-1/2 border-t-2 border-dashed border-border" />
      </div>

      {/* Stub biglietto */}
      <div className={cn('flex items-stretch gap-3', compact ? 'p-3 pt-4' : 'p-4 pt-5')}>
        <div className="min-w-0 flex-1">
          {showFavorite && e.category && (
            <span className="mb-1 inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-primary">
              <Ticket className="h-3 w-3" /><T>{e.category}</T>
            </span>
          )}
          <h3 className={cn('font-display font-semibold text-foreground line-clamp-1', compact ? 'text-sm' : 'text-base')}><T>{e.title}</T></h3>
          {e.owner_name && <p className="mt-0.5 text-xs text-muted-foreground line-clamp-1"><T>{`di ${e.owner_name}`}</T></p>}
          <div className="mt-2 space-y-1 text-xs text-muted-foreground">
            {date && <p className="flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5 text-primary" />{date}</p>}
            {e.location?.name && <p className="flex items-center gap-1.5 line-clamp-1"><MapPin className="h-3.5 w-3.5 shrink-0 text-primary" /><T>{e.location.name}</T></p>}
            {!compact && e.duration_min > 0 && <p className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-primary" />{e.duration_min} min</p>}
          </div>
          <span className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-primary">
            <T>Prenota ora</T> <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </span>
        </div>
        <div className="flex shrink-0 flex-col items-center justify-center rounded-xl border border-border bg-white p-1.5">
          <QRCodeSVG value={url} size={qrSize} fgColor="#1a3f36" bgColor="#ffffff" level="M" title="Codice QR per prenotare l'esperienza" role="img" aria-label="Codice QR per prenotare l'esperienza" />
        </div>
      </div>
    </Link>
  );
}

/** Vetrina "biglietti" delle esperienze reali (mock proposto in sostituzione dei box marketing). */
export function ExperienceTicketShowcase() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    let alive = true;
    experienceService.publicList().then((d) => { if (alive) setItems((d.experiences || []).slice(0, 6)); })
      .catch(() => { if (alive) setItems([]); });
    return () => { alive = false; };
  }, []);

  if (items === null || items.length === 0) return null;

  return (
    <section className="sila-section pb-6" data-testid="experience-ticket-showcase">
      <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-primary">
            <Sparkles className="h-3.5 w-3.5" /><T>I tuoi biglietti in un tap</T>
          </span>
          <h2 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl"><T>Esperienze da vivere</T></h2>
          <p className="mt-2 max-w-2xl text-muted-foreground"><T>Ogni esperienza è un biglietto digitale con QR: scegli, prenota e presentati all'ingresso.</T></p>
        </div>
        <Button asChild variant="outline" className="rounded-full">
          <Link to="/esperienze/catalogo" data-testid="experience-see-all"><T>Vedi tutte</T><ArrowRight className="ml-2 h-4 w-4" /></Link>
        </Button>
      </div>
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((e) => <ExperienceTicketCard key={e.id} e={e} />)}
      </div>
    </section>
  );
}

export default ExperienceTicketShowcase;
